import { PDFEditorConnector } from '@ethsign/pdf-editor';
import { debounce } from 'lodash-es';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Loading } from '@/components';
import { ContractEditor, ContractEditorRef } from '@/components/ContractEditorNew';
import { IContact, IField, SendType, SIGN_MODE_ENUM } from '@/types';

import { eventBus } from '@/utils';

import { LefPanel } from './LefPanel';

interface Props {
  accounts?: IContact[];
  file?: any;
  blankFile?: any;
  xfdfString?: string;
  onChange: (xfdfString: string, annotations: any) => void;
  isShow: boolean;
  fileType: 'pdf' | 'word';
  contractName: string;
  sendType: SendType;
  onContractTypeChange?: (type: SIGN_MODE_ENUM) => void;
  contractType: SIGN_MODE_ENUM;
  fields?: IField[];
  isUseTemplate?: boolean;
}
export const AddField = forwardRef(function AddField(props: Props, ref: any) {
  const {
    fields,
    isShow,
    onChange,
    contractName,
    sendType,
    xfdfString,
    onContractTypeChange,
    blankFile,
    isUseTemplate
  } = props;
  const [instance, setInstance] = useState<PDFEditorConnector>();
  const [loading, setLoading] = useState(true);
  const [annotations, setAnnotations] = useState<any[]>([]);
  const [contractType, setContractType] = useState<SIGN_MODE_ENUM>(SIGN_MODE_ENUM.OneSignOne); // 合同类型
  const pdfEditorRef = useRef<ContractEditorRef>(null);
  const [file, setFile] = useState<any>(props.file);
  const [accounts, setAccoutes] = useState<any>(props.accounts);
  const debounceSetAccoutes = useCallback(
    debounce((accounts: any) => {
      setAccoutes(accounts);
    }, 300),
    []
  );

  useEffect(() => {
    debounceSetAccoutes(props.accounts);
  }, [props.accounts]);
  useEffect(() => {
    setFile(props.file);
  }, [props.file]);

  useEffect(() => {
    const attachAnnots = async () => {
      if (fields?.length && (accounts?.length || contractType === SIGN_MODE_ENUM.OneSignOne) && instance && blankFile) {
        setFile(props.file);
        const newFields = fields
          .filter((item) => item.type !== 'term')
          .map((field) => {
            const custom: any =
              accounts?.find((item: IContact) => {
                let fieldCustom;
                try {
                  fieldCustom = field.custom && JSON.parse(field.custom);
                } catch (error) {
                  fieldCustom = {};
                }
                return item.signerId === fieldCustom?.id;
              }) || {};
            return {
              ...field,
              custom: {
                ...custom,
                label: field.label,
                required: field.required,
                type: field.type,
                address: custom?.walletAddress,
                isTemplateAdd: true
              }
            };
          });
        if (file !== props.file) {
          eventBus.once('documentLoaded', async () => {
            await pdfEditorRef.current?.attachAnnots(newFields, blankFile);
            setFile(blankFile);
          });
        } else {
          await pdfEditorRef.current?.attachAnnots(newFields, blankFile);
          setFile(blankFile);
        }
      }
    };
    if (isShow) {
      attachAnnots();
    }
  }, [instance, JSON.stringify(accounts), blankFile, isShow]);

  useEffect(() => {
    if (contractType !== props.contractType) {
      setContractType(props.contractType);
    }
  }, [props.contractType]);

  useImperativeHandle(ref, () => ({
    getFile: () => {
      return pdfEditorRef.current?.getFile();
    },
    deleteAllAnnotations: () => {
      instance?.removeAllAnnotations();
    },
    attachAnnots: pdfEditorRef.current?.attachAnnots,
    refreshFile: () => {
      setFile(props.file);
    }
  }));

  return (
    <div className={`flex  flex-row h-[calc(100%-156px)] ${isShow ? '' : '!h-0 opacity-0'}`}>
      {loading && isShow && <Loading></Loading>}
      <div className="w-[352px]">
        <LefPanel
          onFieldClick={(data: any) => {
            pdfEditorRef.current?.addAnnotation(data);
          }}
          onAddEmptyPage={() => {
            pdfEditorRef.current?.loadDocument(true);
          }}
          isUseTemplate={isUseTemplate}
          contractType={contractType}
          sendType={sendType}
          annotations={annotations}
          key={JSON.stringify(accounts)}
          onContractTypeChange={(type) => {
            if (contractType === type) return;
            setContractType(type);
            onContractTypeChange && onContractTypeChange(type);
            pdfEditorRef.current?.loadDocument(false);
          }}
          accounts={accounts!}
        ></LefPanel>
      </div>
      <div className="flex-1 h-full relative overflow-hidden">
        <ContractEditor
          type="prepare"
          ref={pdfEditorRef}
          {...props}
          file={file}
          sendType={sendType}
          xfdfString={contractType === SIGN_MODE_ENUM.OneSignOne ? xfdfString : undefined}
          onChange={(xfdfString, annotations) => {
            setAnnotations(annotations);
            onChange(xfdfString, annotations);
          }}
          contractName={contractName}
          onLoaded={(inst) => {
            !instance && setInstance(inst);
            setLoading(false);
          }}
        ></ContractEditor>
      </div>
    </div>
  );
});
